import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box, Card, Grid, Typography, CircularProgress,
  Button, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  IconButton, Avatar, Tooltip, TextField, InputAdornment
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import ViewKanbanIcon from "@material-ui/icons/ViewColumn";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Cell, LabelList
} from "recharts";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: "24px", backgroundColor: "#f0f2f5", minHeight: "100vh", [theme.breakpoints.down("sm")]: { padding: "12px 8px" } },
  header: { marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerButtons: { display: "flex", gap: 8, flexShrink: 0, [theme.breakpoints.down("xs")]: { width: "100%" } },
  title: { fontSize: 26, fontWeight: 800, color: "#1a202c", letterSpacing: "-0.5px", [theme.breakpoints.down("sm")]: { fontSize: 20 } },
  subtitle: { fontSize: 14, color: "#718096", marginTop: 2 },
  filterCard: {
    backgroundColor: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
  },
  metricCard: {
    borderRadius: 14, height: "100%", overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    transition: "transform 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }
  },
  metricInner: { padding: "20px 20px 16px", height: "100%" },
  metricLabel: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 },
  metricValue: { fontSize: 36, fontWeight: 800, lineHeight: 1 },
  metricSub: { fontSize: 12, marginTop: 6, opacity: 0.85 },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, marginTop: 24 },
  chartCard: { borderRadius: 14, backgroundColor: "#fff", padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  chartTitle: { fontSize: 15, fontWeight: 700, color: "#2d3748", marginBottom: 16 },
  tableCard: { borderRadius: 14, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden" },
  tableHead: { backgroundColor: theme.palette.primary.main },
  tableHeadCell: { color: "#fff !important", fontWeight: "700 !important", fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.5px" },
  stageRow: {
    cursor: "pointer",
    transition: "background-color 0.15s",
    "&:hover": { backgroundColor: "#ebf8ff" },
    "&:nth-of-type(even)": { backgroundColor: "#fafafa" }
  },
  pipelineCard: {
    display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 12px",
    borderRadius: 12, border: "2px solid transparent", cursor: "pointer",
    transition: "all 0.2s", height: "100%",
    "&:hover": { borderColor: "var(--stage-color)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }
  },
  ticketCard: {
    borderRadius: 10, padding: "10px 14px", marginBottom: 8, backgroundColor: "#fff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #f0f0f0"
  },
  dialogHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
}));

const fmt = (d) => format(new Date(d), "yyyy-MM-dd");

const KANBAN_COLORS = [
  "#4299e1", "#38a169", "#ed8936", "#9f7aea", "#e53e3e",
  "#d69e2e", "#38b2ac", "#667eea", "#fc8181", "#f6ad55",
  "#68d391", "#76e4f7"
];

export default function RelatorioTagsKanban() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [stageTickets, setStageTickets] = useState([]);
  const [ticketSearch, setTicketSearch] = useState("");
  const [view, setView] = useState("pipeline");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tags", { params: { kanban: 1 } });
      const list = Array.isArray(data) ? data : data?.tags || [];
      setStages(list);
    } catch {
      setStages([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, []);

  const openStage = async (stage) => {
    setSelectedStage(stage);
    setTicketSearch("");
    setLoadingTickets(true);
    setStageTickets([]);
    try {
      const { data } = await api.get("/tickets", {
        params: { tagId: stage.id, pageSize: 100, status: "open" }
      });
      setStageTickets(data?.tickets || data?.data || []);
    } catch {
      setStageTickets([]);
    }
    setLoadingTickets(false);
  };

  const totalStages = stages.length;
  const totalTickets = stages.reduce((s, t) => s + (t.tickets || 0), 0);
  const busierStage = [...stages].sort((a, b) => (b.tickets || 0) - (a.tickets || 0))[0];

  const barData = stages.map((s, i) => ({
    name: (s.name || "").length > 12 ? (s.name || "").slice(0, 10) + "…" : s.name,
    fullName: s.name,
    value: s.tickets || 0,
    color: s.color || KANBAN_COLORS[i % KANBAN_COLORS.length],
    id: s.id
  }));

  const handleExport = () => {
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    const rows = stages.map((s, i) => {
      const pct = totalTickets > 0 ? Math.round((s.tickets || 0) / totalTickets * 100) : 0;
      return `<tr style="cursor:default"><td>${i + 1}</td>
        <td><span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${s.color || KANBAN_COLORS[i % KANBAN_COLORS.length]};margin-right:6px;vertical-align:middle"></span>${s.name}</td>
        <td>${s.tickets || 0}</td><td>${pct}%</td></tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/><title>Relatório Kanban</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;padding:40px;background:#f5f5f5}
h1{font-size:26px;font-weight:800;margin-bottom:4px}p{color:#718096;margin-bottom:24px}
table{width:100%;border-collapse:collapse;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.07)}
th{background:#667eea;color:#fff;padding:12px 16px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;text-align:left}
td{padding:12px 16px;border-bottom:1px solid #f0f0f0;font-size:14px}tr:last-child td{border:none}
.footer{margin-top:32px;color:#a0aec0;font-size:12px}</style></head>
<body><h1>📋 Relatório Kanban</h1><p>Exportado em ${now}</p>
<table><thead><tr><th>#</th><th>Etapa</th><th>Tickets</th><th>Participação</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="footer">Total: ${totalStages} etapas · ${totalTickets} tickets</div></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relatorio-kanban-${fmt(new Date())}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  const filteredTickets = stageTickets.filter(t => {
    if (!ticketSearch) return true;
    const q = ticketSearch.toLowerCase();
    return (t.contact?.name || "").toLowerCase().includes(q) || String(t.id).includes(q);
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box style={{ flex: "1 1 auto" }}>
          <Typography className={classes.title}>Relatório Kanban</Typography>
          <Typography className={classes.subtitle}>Tickets distribuídos por etapas do pipeline</Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}>
            Atualizar
          </Button>
          <Button variant="outlined" size="small" onClick={() => setView(v => v === "pipeline" ? "table" : "pipeline")}
            style={{ borderRadius: 8, textTransform: "none" }}>
            {view === "pipeline" ? "Tabela" : "Pipeline"}
          </Button>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExport}
            disabled={loading || !stages.length}
            style={{ backgroundColor: "#667eea", color: "#fff", borderRadius: 8, textTransform: "none" }}>
            Exportar HTML
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterCard}>
        {loading && <CircularProgress size={20} />}
        {!loading && stages.length > 0 && (
          <Chip label={`${totalStages} etapas · ${totalTickets} tickets no pipeline`} size="small"
            style={{ backgroundColor: "#ebf4ff", color: "#3c366b", fontWeight: 600 }} />
        )}
        {!loading && busierStage && (
          <Chip
            label={`Mais movimentada: ${busierStage.name} (${busierStage.tickets || 0})`}
            size="small"
            style={{ backgroundColor: "#f0fff4", color: "#276749", fontWeight: 600 }}
          />
        )}
      </Box>

      <Typography className={classes.sectionLabel}>Visão Geral</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#ebf4ff,#c3dafe)", borderTop: "3px solid #667eea" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#3c366b" }}>Etapas do Pipeline</Typography>
                <ViewKanbanIcon style={{ color: "#667eea", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#3c366b" }}>{loading ? "—" : totalStages}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#3c366b" }}>Colunas do Kanban</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#fff3e5,#fbd38d)", borderTop: "3px solid #ed8936" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#c05621" }}>Tickets no Pipeline</Typography>
                <ViewKanbanIcon style={{ color: "#ed8936", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#c05621" }}>{loading ? "—" : totalTickets}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#c05621" }}>Em alguma etapa Kanban</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#f0fff4,#c6f6d5)", borderTop: "3px solid #38a169" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#276749" }}>Etapa Mais Cheia</Typography>
                <ViewKanbanIcon style={{ color: "#38a169", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                {busierStage?.color && <Box style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: busierStage.color }} />}
                <Typography className={classes.metricValue} style={{ color: "#276749", fontSize: busierStage?.name?.length > 12 ? 18 : 24 }}>
                  {loading || !busierStage ? "—" : busierStage.name}
                </Typography>
              </Box>
              <Typography className={classes.metricSub} style={{ color: "#276749" }}>
                {busierStage ? `${busierStage.tickets || 0} tickets` : ""}
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Gráfico de barras do pipeline */}
      <Typography className={classes.sectionLabel}>Volume por Etapa</Typography>
      <Card elevation={0} className={classes.chartCard}>
        <Typography className={classes.chartTitle}>Distribuição de Tickets no Pipeline</Typography>
        {loading || barData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Nenhuma etapa Kanban cadastrada</Typography>}
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={barData} margin={{ top: 20, right: 10, left: -10, bottom: 5 }} barSize={38}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#718096" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#a0aec0" }} axisLine={false} tickLine={false} />
              <ReTooltip formatter={(val, _, props) => [`${val} tickets`, props?.payload?.fullName || ""]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}
                onClick={(d) => {
                  const stage = stages.find(s => s.id === d.id);
                  if (stage) openStage(stage);
                }}
                style={{ cursor: "pointer" }}>
                {barData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                <LabelList dataKey="value" position="top" style={{ fontSize: 12, fontWeight: 700, fill: "#4a5568" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        {!loading && barData.length > 0 && (
          <Typography style={{ fontSize: 12, color: "#a0aec0", marginTop: 8 }}>
            Clique em uma barra para ver os tickets dessa etapa
          </Typography>
        )}
      </Card>

      {/* Etapas como cards de pipeline */}
      {view === "pipeline" && !loading && stages.length > 0 && (
        <>
          <Typography className={classes.sectionLabel}>Etapas do Pipeline</Typography>
          <Grid container spacing={2}>
            {stages.map((stage, i) => {
              const color = stage.color || KANBAN_COLORS[i % KANBAN_COLORS.length];
              const pct = totalTickets > 0 ? Math.round((stage.tickets || 0) / totalTickets * 100) : 0;
              return (
                <Grid item xs={6} sm={4} md={3} key={stage.id}>
                  <Card elevation={0} onClick={() => openStage(stage)} style={{ cursor: "pointer", borderRadius: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.07)", transition: "all 0.2s", "&:hover": {} }}>
                    <Box style={{ borderTop: `4px solid ${color}`, padding: "16px" }}>
                      <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 8 }}>
                        <Box style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
                        <Typography style={{ fontWeight: 700, fontSize: 13, color: "#2d3748" }}>{stage.name}</Typography>
                      </Box>
                      <Typography style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{stage.tickets || 0}</Typography>
                      <Typography style={{ fontSize: 11, color: "#a0aec0", marginTop: 4 }}>tickets · {pct}% do total</Typography>
                      <Box style={{ height: 4, backgroundColor: "#e2e8f0", borderRadius: 2, marginTop: 8 }}>
                        <Box style={{ height: "100%", width: `${pct}%`, backgroundColor: color, borderRadius: 2 }} />
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Tabela */}
      {view === "table" && (
        <>
          <Typography className={classes.sectionLabel}>Etapas</Typography>
          <Card elevation={0} className={classes.tableCard}>
            <TableContainer>
              <Table size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell} style={{ width: 50 }}>#</TableCell>
                    <TableCell className={classes.tableHeadCell}>Etapa</TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">Tickets</TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">Participação</TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">Ver Tickets</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ padding: 40 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : stages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ padding: 40, color: "#a0aec0" }}>
                        Nenhuma etapa Kanban cadastrada
                      </TableCell>
                    </TableRow>
                  ) : stages.map((stage, i) => {
                    const color = stage.color || KANBAN_COLORS[i % KANBAN_COLORS.length];
                    const pct = totalTickets > 0 ? Math.round((stage.tickets || 0) / totalTickets * 100) : 0;
                    return (
                      <TableRow key={stage.id} className={classes.stageRow} onClick={() => openStage(stage)}>
                        <TableCell style={{ color: "#a0aec0", fontSize: 12 }}>{i + 1}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Box style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: color }} />
                            <Typography style={{ fontWeight: 600, fontSize: 14 }}>{stage.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={stage.tickets || 0} size="small"
                            style={{ backgroundColor: color + "22", color, fontWeight: 700, minWidth: 44 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography style={{ fontSize: 13, fontWeight: 600 }}>{pct}%</Typography>
                            <Box style={{ height: 4, backgroundColor: "#e2e8f0", borderRadius: 2, marginTop: 3 }}>
                              <Box style={{ height: "100%", width: `${pct}%`, backgroundColor: color, borderRadius: 2 }} />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver tickets desta etapa">
                            <IconButton size="small"><OpenInNewIcon style={{ fontSize: 16, color: "#a0aec0" }} /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* Modal de tickets da etapa */}
      <Dialog open={Boolean(selectedStage)} onClose={() => setSelectedStage(null)} fullWidth maxWidth="sm">
        {selectedStage && (
          <>
            <DialogTitle>
              <Box className={classes.dialogHead}>
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                  <Box style={{
                    width: 14, height: 14, borderRadius: "50%",
                    backgroundColor: selectedStage.color || "#667eea"
                  }} />
                  <Typography style={{ fontWeight: 700, fontSize: 18 }}>
                    Etapa: {selectedStage.name}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setSelectedStage(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <TextField
                placeholder="Buscar por contato ou ID..."
                size="small" variant="outlined" fullWidth
                value={ticketSearch} onChange={e => setTicketSearch(e.target.value)}
                style={{ marginBottom: 16 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: "#a0aec0" }} /></InputAdornment>
                }}
              />
              {loadingTickets ? (
                <Box display="flex" justifyContent="center" padding={4}>
                  <CircularProgress />
                </Box>
              ) : filteredTickets.length === 0 ? (
                <Typography style={{ color: "#a0aec0", textAlign: "center", padding: "24px 0" }}>
                  {ticketSearch ? "Nenhum ticket encontrado" : "Nenhum ticket aberto nesta etapa"}
                </Typography>
              ) : filteredTickets.map(ticket => {
                const contactName = ticket.contact?.name || `Ticket #${ticket.id}`;
                const initials = contactName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
                return (
                  <Box key={ticket.id} className={classes.ticketCard}>
                    <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                      {ticket.contact?.profilePicUrl ? (
                        <Avatar src={ticket.contact.profilePicUrl} style={{ width: 36, height: 36 }} />
                      ) : (
                        <Avatar style={{ width: 36, height: 36, backgroundColor: selectedStage.color || "#667eea", fontSize: 13, fontWeight: 700 }}>
                          {initials}
                        </Avatar>
                      )}
                      <Box flex={1}>
                        <Typography style={{ fontWeight: 700, fontSize: 14 }}>{contactName}</Typography>
                        <Typography style={{ fontSize: 12, color: "#718096" }}>
                          Ticket #{ticket.id}
                          {ticket.lastMessage ? ` · ${(ticket.lastMessage || "").slice(0, 40)}…` : ""}
                        </Typography>
                      </Box>
                      <Chip
                        label={ticket.status === "open" ? "Aberto" : ticket.status === "closed" ? "Fechado" : "Pendente"}
                        size="small"
                        style={{
                          backgroundColor: ticket.status === "open" ? "#f0fff4" : ticket.status === "closed" ? "#f7fafc" : "#fffaf0",
                          color: ticket.status === "open" ? "#276749" : ticket.status === "closed" ? "#718096" : "#c05621",
                          fontWeight: 600, fontSize: 11
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
              <Box style={{ padding: "8px 0 4px" }}>
                <Typography style={{ fontSize: 12, color: "#a0aec0" }}>
                  {filteredTickets.length} ticket{filteredTickets.length !== 1 ? "s" : ""} exibido{filteredTickets.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
